import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, ShieldAlert, Lock, KeyRound, Activity, AlertTriangle,
  CheckCircle2, XCircle, Loader2, RefreshCw, ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const ConfigRow = ({ label, ok, danger, value }) => {
  // `ok` = healthy state present (green check). `danger` = a healthy-state ABSENCE
  // is actually a security RISK (red X). `value` = literal "On / Off / Set / Missing".
  const tone = ok ? 'text-emerald-600' : (danger ? 'text-red-600' : 'text-amber-600');
  const Icon = ok ? CheckCircle2 : (danger ? XCircle : AlertTriangle);
  return (
    <div className="flex items-center justify-between py-2 border-b border-cn-border/60 last:border-0" data-testid={`security-config-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <span className="text-sm text-cn-text">{label}</span>
      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone}`}>
        <Icon className="w-3.5 h-3.5" /> {value}
      </span>
    </div>
  );
};

const CoverageBar = ({ label, total, enabled, pct, testid }) => {
  const tone = pct >= 80 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-cn-text capitalize">{label.replace('_', ' ')}</span>
        <span className="text-cn-text-muted tabular-nums">{enabled}/{total} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-cn-bone-100 rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-all duration-700`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
    </div>
  );
};

/**
 * Admin Security overview — surfaces the "hard infrastructure" institutions
 * want to see during procurement: MFA coverage, abuse-prevention activity,
 * and configuration health. Read-only dashboard backed by
 * `GET /api/admin/security`.
 */
export default function SecurityPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/security`, auth(token));
      setData(d);
    } catch {
      // silent — admin tab shows the empty state
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [load, token]);

  if (loading) {
    return (
      <div className="cn-card p-10 flex items-center justify-center" data-testid="security-loading">
        <Loader2 className="w-5 h-5 animate-spin text-cn-primary" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="cn-card p-10 text-center text-sm text-cn-text-muted" data-testid="security-empty">
        Security overview unavailable.
      </div>
    );
  }

  const cov = data.mfa_coverage || {};
  const conf = data.config_health || {};
  const tot = data.rate_limit_throttles_total_24h || 0;
  const lockouts = data.lockouts_24h || 0;

  return (
    <div className="space-y-5" data-testid="security-panel">
      {/* Action banner */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="cn-btn cn-btn-ghost !py-1.5 text-xs"
          data-testid="security-refresh-btn"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {' '}Refresh
        </button>
      </div>

      {/* MFA coverage */}
      <Card className="bg-white dark:bg-cn-ink-800 border-0">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-base text-cn-text flex items-center gap-2">
                <Lock className="w-4 h-4 text-cn-primary" /> Multi-factor authentication coverage
              </h3>
              <p className="text-xs text-cn-text-muted mt-1">
                TOTP authenticator support is live (Settings → Two-factor authentication). Track coverage across high-privilege roles.
              </p>
            </div>
            <Link
              to="/privacy/dashboard"
              className="text-xs font-semibold text-cn-primary hover:underline inline-flex items-center gap-1"
              data-testid="security-enable-mfa-link"
            >
              Enable on your account <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {['admin', 'clinician', 'agency_admin'].map((role) => {
              const r = cov[role] || { total: 0, mfa_enabled: 0, pct: 0 };
              if (r.total === 0) return null;
              return <CoverageBar key={role} label={role} {...r} enabled={r.mfa_enabled} testid={`security-mfa-${role}`} />;
            })}
          </div>
        </CardContent>
      </Card>

      {/* Abuse prevention — rate-limit + lockout activity */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-white dark:bg-cn-ink-800 border-0">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-base text-cn-text flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-cn-primary" /> Abuse prevention · 24h
            </h3>
            <p className="text-xs text-cn-text-muted mb-3">
              Rate-limiting + login-lockout activity. Spikes indicate either credential-stuffing or a misbehaving integration.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="security-tile-lockouts">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Login lockouts</p>
                <p className="cn-display text-2xl text-cn-text mt-1 tabular-nums">{lockouts}</p>
              </div>
              <div data-testid="security-tile-throttles">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Rate-limit throttles</p>
                <p className="cn-display text-2xl text-cn-text mt-1 tabular-nums">{tot}</p>
              </div>
            </div>
            <div className="mt-4 space-y-1">
              {Object.entries(data.rate_limit_throttles_24h || {}).map(([scope, n]) => (
                <div key={scope} className="flex items-center justify-between text-[11px]">
                  <span className="text-cn-text-muted">{scope.replace(/_/g, ' ')}</span>
                  <span className={`font-bold ${n > 0 ? 'text-amber-700' : 'text-cn-text-muted'}`}>{n}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-cn-ink-800 border-0">
          <CardContent className="p-5">
            <h3 className="font-display font-bold text-base text-cn-text flex items-center gap-2 mb-1">
              <KeyRound className="w-4 h-4 text-cn-primary" /> Webhook delivery health · 7d
            </h3>
            <p className="text-xs text-cn-text-muted mb-3">
              Sustained failures often surface a customer endpoint that has rotated keys without updating CaraLoom.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="security-tile-webhook-failed">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Failed deliveries</p>
                <p className="cn-display text-2xl text-cn-text mt-1 tabular-nums">{data.webhook_failures_7d || 0}</p>
              </div>
              <div data-testid="security-tile-webhook-exhausted">
                <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Retries exhausted</p>
                <p className="cn-display text-2xl text-cn-text mt-1 tabular-nums">{data.webhook_exhausted_7d || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Config health */}
      <Card className="bg-white dark:bg-cn-ink-800 border-0">
        <CardContent className="p-5">
          <h3 className="font-display font-bold text-base text-cn-text flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-cn-primary" /> Configuration health
          </h3>
          <ConfigRow label="JWT secret"               ok={conf.jwt_secret_set}                  value={conf.jwt_secret_set ? 'Strong (≥32)' : 'Weak'} />
          <ConfigRow label="CORS allowlist"           ok={conf.cors_origins_explicit}           value={conf.cors_origins_explicit ? 'Explicit' : 'Wildcard'} />
          <ConfigRow label="Resend (email)"           ok={conf.resend_configured}               value={conf.resend_configured ? 'Configured' : 'Missing'} />
          <ConfigRow label="Stripe API key"           ok={conf.stripe_configured}               value={conf.stripe_configured ? 'Configured' : 'Missing'} />
          <ConfigRow label="Stripe Connect webhook"   ok={conf.stripe_connect_webhook_secret_set} value={conf.stripe_connect_webhook_secret_set ? 'Signed' : 'Unsigned'} />
          <ConfigRow label="Scheduler (nightly + digest)" ok={conf.scheduler_enabled} value={conf.scheduler_enabled ? 'Running' : 'Paused'} />
          <ConfigRow label="EXPOSE_RESET_TOKEN"       ok={!conf.expose_reset_token} danger={conf.expose_reset_token} value={conf.expose_reset_token ? 'Exposed (DEV)' : 'Off'} />
          <ConfigRow label="EXPOSE_MAGIC_CODE"        ok={!conf.expose_magic_code}  danger={conf.expose_magic_code}  value={conf.expose_magic_code  ? 'Exposed (DEV)' : 'Off'} />
        </CardContent>
      </Card>

      {/* Deep link to the Documentation Safety Shield queue */}
      <Link
        to="/admin/safety-shield"
        className="cn-card p-5 flex items-start justify-between gap-4 hover:bg-cn-bone-50 transition-colors"
        data-testid="security-link-doc-shield"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-cn-primary text-white flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-4 h-4" />
          </div>
          <div>
            <p className="font-display font-bold text-base text-cn-text">Documentation Safety queue</p>
            <p className="text-xs text-cn-text-muted mt-1">
              Per-note NLP scans flagged for supervisor review (separate from the vitals-monitoring queue above).
            </p>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-cn-text-muted flex-shrink-0 mt-2" />
      </Link>

      <p className="text-[11px] text-cn-text-muted mt-2">As of {data.as_of?.slice(0, 19).replace('T', ' ')} UTC</p>
    </div>
  );
}
